import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const CoreServices = () => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState<number | null>(null);

  const services = [
    {
      titleKey: 'svc.item1Title', descKey: 'svc.item1Desc', tagKey: 'svc.item1Tag',
      icon: 'ri-palette-line',
      image: 'https://public.readdy.ai/ai/img_res/0e6f22c4f9101d71f4dce4a62668f6c9.jpg',
    },
    {
      titleKey: 'svc.item2Title', descKey: 'svc.item2Desc', tagKey: 'svc.item2Tag',
      icon: 'ri-code-s-slash-line',
      image: 'https://readdy.ai/api/search-image?query=professional%20web%20developer%20coding%20environment%20with%20multiple%20ultra-wide%20dark%20monitors%20displaying%20clean%20React%20TypeScript%20code%20and%20modern%20UI%20component%20library%2C%20premium%20developer%20workstation%20setup%20with%20mechanical%20keyboard%2C%20dark%20ambient%20room%20lighting%20with%20subtle%20red%20LED%20accent%20glow%20reflecting%20on%20desk%20surface%2C%20high-end%20tech%20workspace%20photography%2C%20sharp%20focus&width=480&height=320&seq=svc002v3&orientation=landscape',
    },
    {
      titleKey: 'svc.item3Title', descKey: 'svc.item3Desc', tagKey: 'svc.item3Tag',
      icon: 'ri-smartphone-line',
      image: 'https://readdy.ai/api/search-image?query=sleek%20premium%20smartphone%20mockup%20displaying%20beautifully%20designed%20dark-themed%20mobile%20application%20interface%20with%20modern%20UI%20screens%2C%20elegant%20product%20photography%20on%20pure%20dark%20background%20with%20subtle%20gradient%2C%20professional%20mobile%20app%20showcase%20with%20crisp%20screen%20details%2C%20minimalist%20tech%20advertising%20aesthetic%2C%20studio%20lighting%2C%20sharp&width=480&height=320&seq=svc003v3&orientation=landscape',
    },
    {
      titleKey: 'svc.item4Title', descKey: 'svc.item4Desc', tagKey: 'svc.item4Tag',
      icon: 'ri-brain-line',
      image: 'https://readdy.ai/api/search-image?query=stunning%20artificial%20intelligence%20neural%20network%203D%20visualization%20with%20thousands%20of%20glowing%20interconnected%20nodes%20and%20synaptic%20connections%20spreading%20across%20deep%20dark%20void%20background%2C%20vibrant%20crimson%20red%20and%20white%20energy%20pulses%20flowing%20through%20data%20pathways%2C%20futuristic%20machine%20learning%20concept%20art%2C%20dramatic%20sci-fi%20technology%20aesthetic%2C%20ultra%20detailed%20render&width=480&height=320&seq=svc004v3&orientation=landscape',
    },
    {
      titleKey: 'svc.item5Title', descKey: 'svc.item5Desc', tagKey: 'svc.item5Tag',
      icon: 'ri-line-chart-line',
      image: 'https://readdy.ai/api/search-image?query=sophisticated%20digital%20marketing%20analytics%20command%20center%20with%20multiple%20dark%20screens%20displaying%20real-time%20growth%20charts%20KPI%20dashboards%20social%20media%20metrics%20and%20campaign%20performance%20data%2C%20professional%20business%20intelligence%20interface%20with%20red%20accent%20color%20scheme%2C%20modern%20data-driven%20marketing%20strategy%20workspace%2C%20cinematic%20dark%20atmosphere&width=480&height=320&seq=svc005v3&orientation=landscape',
    },
    {
      titleKey: 'svc.item6Title', descKey: 'svc.item6Desc', tagKey: 'svc.item6Tag',
      icon: 'ri-settings-4-line',
      image: 'https://readdy.ai/api/search-image?query=premium%20IT%20consulting%20executive%20boardroom%20with%20large%20digital%20strategy%20diagrams%20and%20digital%20transformation%20roadmap%20displayed%20on%20dark%20glass%20wall%2C%20confident%20tech%20executives%20in%20professional%20attire%20engaged%20in%20strategic%20planning%20session%2C%20modern%20dark%20corporate%20interior%20design%20with%20architectural%20lighting%20and%20red%20accent%20elements%2C%20premium%20business%20photography&width=480&height=320&seq=svc006v3&orientation=landscape',
    },
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div>
            <p className="text-brand-red font-semibold text-sm uppercase tracking-widest mb-3">{t('svc.label')}</p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight">
              {t('svc.title')}
            </h2>
          </div>
          <Link to="/services">
            <button className="group flex items-center gap-2 px-6 py-3 border-2 border-gray-200 hover:border-brand-red text-gray-700 hover:text-brand-red font-semibold rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer">
              {t('svc.viewAll')}
              <i className="ri-arrow-right-line rtl:rotate-180 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform"></i>
            </button>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, i) => (
            <div
              key={service.titleKey}
              className="group relative rounded-2xl overflow-hidden cursor-default bg-gray-900 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={service.image}
                  alt={t(service.titleKey)}
                  width={480}
                  height={320}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent"></div>
                <div className="absolute top-4 left-4 rtl:left-auto rtl:right-4 px-3 py-1 bg-brand-red rounded-full text-white text-xs font-bold uppercase tracking-wide">
                  {t(service.tagKey)}
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl">
                    <i className={`${service.icon} text-brand-red text-xl`}></i>
                  </div>
                  <h3 className="font-bold text-white text-lg">{t(service.titleKey)}</h3>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">{t(service.descKey)}</p>

                <div className="mt-5 flex items-center gap-2 text-brand-red text-sm font-semibold opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                  {t('svc.learnMore')} <i className="ri-arrow-right-line rtl:rotate-180"></i>
                </div>
              </div>

              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-red transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CoreServices;
